import React from 'react';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import { WebUiButton } from '../../components/button';
import { WebUiInput } from '../../components/input';
import WebUiPageWrapper from '../../components/page-wrapper';
import { WebUiSelect } from '../../components/select';
import { useWebUiGlobalContext } from '../../context/global/use-global-context';
import type { PoeCfgRow } from '../status/status-poe-utils';
import { normalizePoePortArray } from '../status/status-poe-utils';
import {
  cfgToForm,
  PRIO_OPTIONS,
  POE_PORT_LABELS,
  type PoePortForm,
  parsePoeLimitMode,
  type PoeLimitMode,
  formatLimitDisplay,
  LIMIT_CLASS_OPTIONS,
  NOMINAL_PWR_OPTIONS,
  computeBudgetSumParts,
} from './poe-settings-utils';

export default function WebUiSettingsPoePage() {
  const { currentUser } = useWebUiGlobalContext();
  const readonly = currentUser?.userLevel === 1;
  const [limitText, setLimitText] = React.useState('-');
  const [limitMode, setLimitMode] = React.useState<PoeLimitMode>({
    kind: 'single',
    budget: 0,
  });
  const [rows, setRows] = React.useState<PoePortForm[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    const [limitData, cfgRaw] = await Promise.all([
      postWebUiApi<unknown>({
        service: 'poe',
        access: 'get',
        command: 'get limit',
      }),
      postWebUiApi<unknown>({
        service: 'poe',
        access: 'get',
        command: 'get port cfg state',
      }),
    ]);
    setLimitText(formatLimitDisplay(limitData));
    setLimitMode(parsePoeLimitMode(limitData));
    const cfg = normalizePoePortArray<PoeCfgRow>(cfgRaw);
    setRows(cfg.map((c) => cfgToForm(c)));
  }, []);

  React.useEffect(() => {
    reload().catch((err) => setError((err as Error).message));
  }, [reload]);

  const sumParts = React.useMemo(
    () => computeBudgetSumParts(rows, limitMode),
    [rows, limitMode],
  );

  const patch = (idx: number, p: Partial<PoePortForm>) => {
    setRows((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, ...p } : row)),
    );
  };

  const save = async () => {
    setMessage(null);
    setError(null);
    try {
      await postWebUiApi({
        service: 'poe',
        command: 'set port cfg state',
        access: 'set',
        parameters: rows.map((r) => ({
          name: r.name,
          enable: r.enable,
          limit: r.limit,
          limit_pwr: r.limit_pwr,
          prio: r.prio,
          fault: r.fault,
        })),
      });
      setMessage('Port settings saved.');
      await reload();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <WebUiPageWrapper titleList={['Settings', 'PoE']}>
      {message ? <WebUiAlert type="success" message={message} /> : null}
      {error ? <WebUiAlert type="error" message={error} /> : null}
      <div className="flex w-full flex-row gap-2">
        <div className="flex w-1/3 flex-col gap-2">
          <h4 className="font-bold">Global Settings</h4>
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <th>Settings</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr className="webui-table-row-content">
                <td>Power Budget</td>
                <td>{limitText}</td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Power Budget Setting</td>
                <td>
                  {sumParts.map((p, i) => (
                    <React.Fragment key={i}>
                      {i > 0 ? ' / ' : null}
                      <span
                        className={p.over ? 'text-red-500' : undefined}
                      >{`${p.total}W`}</span>
                    </React.Fragment>
                  ))}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="flex w-2/3 flex-col gap-2">
          <h4 className="font-bold">PD Settings</h4>
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <th>Port</th>
                <th>Powered Device Name</th>
                <th>Enable PoE</th>
                <th>Limit Class</th>
                <th>nominal Power Limit</th>
                <th>Priority</th>
                <th>ITxPT xstatus</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="webui-table-row-content">
                  <td>{POE_PORT_LABELS[i] ?? `P${i + 1}`}</td>
                  <td>
                    <WebUiInput
                      inputSize="sm"
                      className="w-full max-w-[200px]"
                      disabled={readonly}
                      value={r.name}
                      onChange={(e) => patch(i, { name: e.target.value })}
                    />
                  </td>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      disabled={readonly}
                      checked={r.enable}
                      onChange={(e) => patch(i, { enable: e.target.checked })}
                    />
                  </td>
                  <td>
                    <WebUiSelect
                      inputSize="sm"
                      className="w-full min-w-[140px]"
                      disabled={readonly}
                      options={LIMIT_CLASS_OPTIONS}
                      value={String(r.limit)}
                      onChange={(e) =>
                        patch(i, { limit: Number(e.target.value) })
                      }
                    />
                  </td>
                  <td>
                    <WebUiSelect
                      inputSize="sm"
                      className="w-full min-w-[120px]"
                      disabled={readonly}
                      options={NOMINAL_PWR_OPTIONS}
                      value={String(r.limit_pwr)}
                      onChange={(e) =>
                        patch(i, { limit_pwr: Number(e.target.value) })
                      }
                    />
                  </td>
                  <td>
                    <WebUiSelect
                      inputSize="sm"
                      className="w-full min-w-[100px]"
                      disabled={readonly}
                      options={PRIO_OPTIONS}
                      value={String(r.prio)}
                      onChange={(e) =>
                        patch(i, { prio: Number(e.target.value) })
                      }
                    />
                  </td>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      disabled={readonly}
                      checked={r.fault}
                      onChange={(e) => patch(i, { fault: e.target.checked })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!readonly && (
            <div>
              <WebUiButton onClick={() => save().catch(() => {})}>
                Set Port Settings
              </WebUiButton>
            </div>
          )}
        </div>
      </div>
    </WebUiPageWrapper>
  );
}
